// Aviso ÚNICO «Reordenamos el menú» (F4 reorden, 24-oct-2026).
// Solo a quien ya conocía el panel: operadores que ya habían visto el tour de bienvenida ANTES del
// reorden (users.tours.manager_panel_v1 con fecha anterior). A los demás se los enseña el propio tour,
// que apunta al menú nuevo. Se cierra con «Entendido» y queda marcado en el servidor con la misma
// clave/endpoint que los tours (cruza dispositivos), así no vuelve a salir.
import { useState, type FC } from 'react';
import { Compass, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { markTourSeen } from '../../lib/tour/api';
import { MANAGER_TOUR_KEY } from '../ManagerTour';

export const AVISO_MENU_KEY = 'menu_reorden_20261024';
// Día siguiente a la publicación: un tour visto desde esta fecha ya enseñó el menú nuevo.
const CORTE = '2026-10-25';

// Guard a nivel módulo: el contexto no refresca `tours` tras marcar visto y el dashboard se remonta al navegar.
let cerradoEnSesion = false;

const MOVIMIENTOS: Array<{ grupo: string; que: string }> = [
  { grupo: 'Visitas', que: 'Visitas por QR, Escanear tarjeta, Canjear Recompensas y Gestionar Puntos' },
  { grupo: 'Programa', que: 'Recompensas, Membresías, Gift Cards, Referidos, Etiquetas y Tareas' },
  { grupo: 'Mensajes', que: 'Campañas, Mensajes automáticos y WhatsApp del negocio (antes «WhatsApp Directo»)' },
  { grupo: 'Ajustes', que: 'Equipo, Suscripción y Mi cuenta (antes «Configuración»)' },
];

const AvisoMenuNuevo: FC = () => {
  const { currentUser, tours } = useAuth();
  const [cerrado, setCerrado] = useState(cerradoEnSesion);

  const esOperador = !!currentUser && currentUser.role !== 'admin';
  const tourVisto = tours ? tours[MANAGER_TOUR_KEY] : null;
  const yaConociaElPanel = typeof tourVisto === 'string' && tourVisto < CORTE;
  const yaLoVio = !!tours && !!tours[AVISO_MENU_KEY];
  if (cerrado || !esOperador || !yaConociaElPanel || yaLoVio) return null;

  const cerrar = () => {
    cerradoEnSesion = true;
    setCerrado(true);
    void markTourSeen(AVISO_MENU_KEY, { completed: true });
  };

  return (
    <div
      className="relative flex items-start gap-3 rounded-lg border-l-4 border-accent bg-blue-50 dark:bg-accent/10 p-4 shadow-sm animate-fade-in-up"
      role="status"
      data-testid="aviso-menu-nuevo"
    >
      <Compass className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-content">Reordenamos el menú: ahora va por lo que quieres hacer</h3>
        <p className="mt-0.5 text-sm text-content-2">
          Nada se quitó y las direcciones son las mismas; solo cambió de grupo. Dónde quedó cada cosa:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-content-2">
          {MOVIMIENTOS.map((m) => (
            <li key={m.grupo}>
              <span className="font-semibold uppercase tracking-wide text-[11px] text-content">{m.grupo}</span>
              <span className="mx-1.5 text-content-3">·</span>
              {m.que}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-content-3">
          Los ajustes de Gift Cards y de Referidos están dentro de su propia pantalla, en la pestaña «Ajustes».
          Los mensajes automáticos están todos juntos en una sola lista.
        </p>
        <button
          type="button"
          onClick={cerrar}
          className="mt-3 inline-flex items-center rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          data-testid="aviso-menu-cerrar"
        >
          Entendido
        </button>
      </div>
      <button
        type="button"
        onClick={cerrar}
        aria-label="Cerrar aviso"
        className="flex-shrink-0 text-content-3 hover:text-content transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AvisoMenuNuevo;
